import * as React from 'react';
import { approvalsHooks } from '../network/endpoints.js';
import { useOrgUnits, type OrgUnit } from '../../org/network/org.api.js';
import type { ApprovalItem, ApprovalType } from '../types.js';
import { ApprovalDetailDrawer } from './ApprovalDetailDrawer.js';
import { PageHeader } from '../../../components/layout/PageHeader.js';
import { Money } from '../../../components/ui/Money.js';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../lib/locale.js';
import { Button } from '../../../components/ui/Button.js';
import { useDrawerParam } from '../../../lib/hooks';

export function ApprovalsQueue() {
  const [page, setPage] = React.useState(1);
  const [limit] = React.useState(10);
  const [selectedType, setSelectedType] = React.useState<string>('');
  const [selectedBranch, setSelectedBranch] = React.useState<string>('');

  const { id: selectedItemId, extra: { drawerType }, open: openDrawer, close: closeDrawer } = useDrawerParam('drawer', ['drawerType']);
  const selectedItemType = drawerType as ApprovalType | null;

  // Fetch pending approvals
  const { data, isLoading, refetch } = approvalsHooks.useList({
    page,
    limit,
    type: selectedType || undefined,
    orgUnitId: selectedBranch || undefined,
  });

  // Fetch branch list for filter
  const { data: orgUnitsResponse } = useOrgUnits();
  const branches: OrgUnit[] = (orgUnitsResponse as OrgUnit[]) || [];

  const handleRowClick = (item: ApprovalItem) => {
    openDrawer(item.id, { drawerType: item.type });
  };

  const handleCloseDrawer = () => {
    closeDrawer();
  };

  const computeAge = (createdAt: string) => {
    const diffTime = Math.abs(Date.now() - new Date(createdAt).getTime());
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    if (diffHours < 24) {
      return `${diffHours}h`;
    }
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d`;
  };

  const list = data?.data || [];
  const meta = data?.meta;

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Pending Maker-Checker Approvals"
        subtitle={meta ? `${meta.total} pending request${meta.total === 1 ? '' : 's'} awaiting four-eyes review` : undefined}
      />
      <div className="flex-1 w-full min-h-0">
      {/* Filters Bar */}
      <div className="flex flex-wrap gap-4 items-center justify-between mb-6 bg-surface-2 p-4 rounded-xl border border-border">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Operation Type Filter */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-text-secondary uppercase">Type</label>
            <select
              value={selectedType}
              onChange={(e) => {
                setSelectedType(e.target.value);
                setPage(1);
              }}
              className="bg-surface-1 border border-border text-text-primary px-3 py-1.5 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-accent-500 font-semibold"
            >
              <option value="">All Types</option>
              <option value="AUCTION_RESULT_CORRECTED">Auction Result Correction</option>
              <option value="BANK_ACCOUNT_UPDATE">Bank Account Update</option>
              <option value="CONFIG_CHANGE">System Config Change</option>
              <option value="EXIT_REFUND">Exit Refund</option>
              <option value="GENERAL_TRANSACTION_POST">General Transaction</option>
              <option value="PAYOUT_REVERSED">Payout Reversal</option>
              <option value="RECEIPT_REVERSAL">Receipt Reversal</option>
              <option value="RECEIPT_VOUCHER_CREATE">Receipt Voucher Creation</option>
              <option value="SCHEME_REGISTRATION">Scheme Registration</option>
              <option value="SETTLEMENT_DISBURSEMENT">Settlement Disbursement</option>
              <option value="SUBSCRIBER_TRANSFER">Subscriber Name Transfer</option>
            </select>
          </div>

          {/* Branch / Org Unit Filter */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-text-secondary uppercase">Branch / Org Unit</label>
            <select
              value={selectedBranch}
              onChange={(e) => {
                setSelectedBranch(e.target.value);
                setPage(1);
              }}
              className="bg-surface-1 border border-border text-text-primary px-3 py-1.5 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-accent-500 font-semibold"
            >
              <option value="">All Branches</option>
              {branches.map((b: OrgUnit) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <Button
          variant="primary"
          onClick={() => refetch()}
          className="flex items-center gap-1"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 12H16" />
          </svg>
          Refresh
        </Button>
      </div>

      {/* Queue Data Table */}
      <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-surface-2">
            <tr>
              <th className="px-6 py-3.5 text-left text-xs font-bold text-text-secondary uppercase tracking-wider">Type</th>
              <th className="px-6 py-3.5 text-left text-xs font-bold text-text-secondary uppercase tracking-wider">Human-Readable Summary</th>
              <th className="px-6 py-3.5 text-left text-xs font-bold text-text-secondary uppercase tracking-wider">Maker</th>
              <th className="px-6 py-3.5 text-left text-xs font-bold text-text-secondary uppercase tracking-wider">Submitted</th>
              <th className="px-6 py-3.5 text-left text-xs font-bold text-text-secondary uppercase tracking-wider">Amount</th>
              <th className="px-6 py-3.5 text-left text-xs font-bold text-text-secondary uppercase tracking-wider">Branch</th>
              <th className="px-6 py-3.5 text-left text-xs font-bold text-text-secondary uppercase tracking-wider">Age</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-text-secondary font-medium">
                  Loading pending approvals...
                </td>
              </tr>
            ) : list.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-text-secondary font-medium">
                  No pending approvals found in your queue.
                </td>
              </tr>
            ) : (
              list.map((item) => {
                const branchName = branches.find((b: any) => b.id === item.orgUnitId)?.name || 'HQ/General';
                return (
                  <tr
                    key={item.id}
                    onClick={() => handleRowClick(item)}
                    className="hover:bg-surface-2 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="inline-flex items-center rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-semibold text-accent-700 font-mono">
                        {item.type.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-text-primary">
                      {item.summary}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary font-medium">
                      {item.makerEmail}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary font-medium">
                      {new Date(item.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-text-primary">
                      {item.amount !== null ? (
                        <Money paise={item.amount} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                      ) : (
                        <span className="text-text-muted">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary font-medium">
                      {branchName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary font-semibold font-mono">
                      {computeAge(item.createdAt)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {meta && meta.totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 bg-surface-2 p-4 rounded-xl border border-border">
          <div className="text-sm font-semibold text-text-secondary">
            Showing Page {meta.page} of {meta.totalPages} ({meta.total} items)
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              onClick={() => setPage((prev) => Math.min(prev + 1, meta.totalPages))}
              disabled={page === meta.totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Approval Detail slide-over drawer */}
      <ApprovalDetailDrawer
        isOpen={selectedItemId !== null && selectedItemType !== null}
        onClose={handleCloseDrawer}
        itemId={selectedItemId}
        itemType={selectedItemType}
        onSuccess={() => refetch()}
      />
      </div>
    </div>
  );
}
